import Intercom, { hide, show, update } from '@intercom/messenger-js-sdk';
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';

import { useCallback, useEffect } from 'react';

import { APP_ENVS } from '@/shared/constants/envs';

type UseIntercomWidgetReturn = {
	initializeIntercom: (options: { autoHide?: boolean; email?: string; name?: string }) => void;
	hideIntercomLauncher: () => void;
	showIntercomLauncher: () => void;
	openIntercomConversation: () => void;
	closeIntercomConversation: () => void;
	updateIntercom: (data?: Partial<IntercomSettings>) => void;
};

function useIntercomWidget(): UseIntercomWidgetReturn {
	const initializeIntercom = useCallback(
		(options: { autoHide?: boolean; email?: string; name?: string }) => {
			const appId = APP_ENVS.INTERCOM_APP_ID;
			if (!appId) {
				console.warn('Intercom app ID is required');
				return;
			}

			Intercom({
				app_id: appId,
				hide_default_launcher: options.autoHide ?? false,
				email: options.email,
				name: options.name,
			});
		},
		[]
	);

	const hideIntercomLauncher = useCallback(() => {
		update({ hide_default_launcher: true });
	}, []);

	const showIntercomLauncher = useCallback(() => {
		update({ hide_default_launcher: false });
	}, []);

	const openIntercomConversation = useCallback(() => {
		show();
	}, []);

	const closeIntercomConversation = useCallback(() => {
		hide();
	}, []);

	const updateIntercom = useCallback((data?: Partial<IntercomSettings>) => {
		update({ ...data });
	}, []);

	return {
		initializeIntercom,
		hideIntercomLauncher,
		showIntercomLauncher,
		openIntercomConversation,
		closeIntercomConversation,
		updateIntercom,
	};
}

/** Boots Intercom after `load`, then idle (idle alone can fire before `load` when streaming). */
export function useIdleIntercom() {
	const { initializeIntercom } = useIntercomWidget();

	useEffect(() => {
		let cancelScheduled = () => {};
		const boot = () => initializeIntercom({ autoHide: false });

		const scheduleWhenIdle = () => {
			if (typeof window.requestIdleCallback !== 'function') {
				const timeout = window.setTimeout(boot, 3000);
				cancelScheduled = () => window.clearTimeout(timeout);
				return;
			}
			const handle = window.requestIdleCallback(boot, { timeout: 5000 });
			cancelScheduled = () => window.cancelIdleCallback(handle);
		};

		if (document.readyState === 'complete') scheduleWhenIdle();
		else window.addEventListener('load', scheduleWhenIdle, { once: true });

		return () => {
			window.removeEventListener('load', scheduleWhenIdle);
			cancelScheduled();
		};
	}, [initializeIntercom]);
}

export default useIntercomWidget;
